/**
 * +grid: hand-authored Waystone Grid data: notables, keystones, stat values and names, and the
 * per-region recipe the layout builder (layout.ts) turns into ~320 nodes. Every name is original
 * (names.ts: GRID_DENYLIST keeps Path of Exile / FFX names out, WOW_NAMES keeps WoW out).
 */
import type { GridIdle, GridStat, GridTheme, KeystoneDef, NotableDef, SlotMod, SlotSelector } from './types';

/** Class regions clockwise from the top. Neighbours blend: Warrior–Rogue, Rogue–Ranger, Ranger–Mage, Mage–Acolyte, Acolyte–Warrior. */
export const GRID_CLASS_ORDER = ['warrior', 'rogue', 'ranger', 'mage', 'acolyte'] as const;

/**
 * Value of one inner-ring minor node of each stat (fractions: 0.006 = +0.6%). Middle-ring minors give ×1.5.
 * Tuned so the full level-10 suggested path (32 points: ~22 minors, 4 notables, 3 slots) adds ≈ +25–30%
 * (harness `specs`; docs/design/waystone-grid.md "Balance"): minors ≈ +12%, notables ≈ +10%, slots ≈ +4%.
 */
export const GRID_STAT_VALUE: Readonly<Record<GridStat, number>> = {
  maxHp: 0.008,
  damage: 0.006,
  healing: 0.007,
  haste: 0.005,
  crit: 0.005,
  armor: 0.006,
  dodge: 0.004,
  regen: 0.015,
  cooldown: 0.006,
  leech: 0.003,
  healingTaken: 0.008,
  moveSpeed: 0.008,
};
export const GRID_MIDDLE_MULT = 1.5;

export const GRID_STAT_NAME: Readonly<Record<GridStat, string>> = {
  maxHp: 'Vigour',
  damage: 'Keenness',
  healing: 'Mending',
  haste: 'Quickness',
  crit: 'Sharp Eye',
  armor: 'Ironhide',
  dodge: 'Lightfoot',
  regen: 'Deep Wells',
  cooldown: 'Swift Recovery',
  leech: 'Siphon',
  healingTaken: 'Warm Blood',
  moveSpeed: 'Fleetness',
};

/** Tooltip line for a stat (value is the fraction). */
export const GRID_STAT_TEXT: Readonly<Record<GridStat, (v: number) => string>> = {
  maxHp: (v) => `+${pct(v)} maximum health`,
  damage: (v) => `+${pct(v)} damage`,
  healing: (v) => `+${pct(v)} healing done`,
  haste: (v) => `+${pct(v)} haste`,
  crit: (v) => `+${pct(v)} critical strike chance`,
  armor: (v) => `+${pct(v)} armour (physical damage taken)`,
  dodge: (v) => `+${pct(v)} dodge chance`,
  regen: (v) => `+${pct(v)} resource regeneration`,
  cooldown: (v) => `Cooldowns recover ${pct(v)} faster`,
  leech: (v) => `Heal for ${pct(v)} of the damage you deal`,
  healingTaken: (v) => `+${pct(v)} healing received`,
  moveSpeed: (v) => `+${pct(v)} movement speed`,
};

/** Value of one utility node. */
export const GRID_IDLE_VALUE: Readonly<Record<GridIdle, number>> = {
  gatherSpeed: 0.08,
  goldFind: 0.1,
  autoWits: 1,
  oocSpeed: 0.08,
  profSkillUp: 0.1,
  offlineRate: 0.05,
};

export const GRID_IDLE_NAME: Readonly<Record<GridIdle, string>> = {
  gatherSpeed: 'Nimble Hands',
  goldFind: 'Coin Sense',
  autoWits: 'Seasoned Instinct',
  oocSpeed: "Wayfarer's Stride",
  profSkillUp: 'Craftlore',
  offlineRate: 'Long Watch',
};

export const GRID_IDLE_TEXT: Readonly<Record<GridIdle, (v: number) => string>> = {
  gatherSpeed: (v) => `Gather herbs and ore ${pct(v)} faster`,
  goldFind: (v) => `+${pct(v)} gold from kills`,
  autoWits: () => 'Auto plays smarter: rests, heals and dodges a little earlier',
  oocSpeed: (v) => `+${pct(v)} run speed out of combat`,
  profSkillUp: (v) => `+${pct(v)} profession skill-up chance`,
  offlineRate: (v) => `+${pct(v)} offline gains (wakes in a later update)`,
};

/** Idle stats that do nothing yet (Phase 2): shown, counted, never compiled. */
export const GRID_IDLE_DORMANT: readonly GridIdle[] = ['offlineRate'];

export const GRID_THEME_NAME: Readonly<Record<GridTheme, string>> = {
  might: 'Might',
  finesse: 'Finesse',
  arcana: 'Arcana',
  endurance: 'Endurance',
  precision: 'Precision',
  hearth: 'Hearth Ring',
};

// ---------------------------------------------------------------------------
// Slot modifiers: names
// ---------------------------------------------------------------------------

const SEL_WORD: Readonly<Record<string, string>> = {
  '1': 'First Star',
  '2': 'Second Star',
  '3': 'Third Star',
  '4': 'Fourth Star',
  '5': 'Fifth Star',
  '6': 'Sixth Star',
  '7': 'Seventh Star',
  aoe: 'Burst',
  defensive: 'Ward',
  spender: 'Crown',
  builder: 'Spark',
  movement: 'Stride',
  interrupt: 'Hush',
  heal: 'Balm',
};
const MOD_WORD: Readonly<Record<SlotMod['kind'], string>> = {
  damage: 'Honed',
  cooldown: 'Hastened',
  cost: 'Thrifty',
  crit: 'Piercing',
  castSpeed: 'Quickened',
  charge: 'Twinned',
  extraTarget: 'Widening',
  root: 'Binding',
};

const selKey = (s: SlotSelector): string => ('key' in s ? s.key : s.tag);
export function slotNodeName(sel: SlotSelector, mod: SlotMod): string {
  return `${MOD_WORD[mod.kind]} ${SEL_WORD[selKey(sel)] ?? selKey(sel)}`;
}

/** "your key-2 ability" / "your AoE ability". */
export function slotSelectorText(sel: SlotSelector): string {
  if ('key' in sel) return `your key-${sel.key} ability`;
  const t: Record<string, string> = { aoe: 'your AoE ability', defensive: 'your defensive ability', spender: 'your spender', builder: 'your builder', movement: 'your movement ability', interrupt: 'your interrupt', heal: 'your heal' };
  return t[sel.tag] ?? `your ${sel.tag} ability`;
}

export function slotModText(sel: SlotSelector, mod: SlotMod): string {
  const who = slotSelectorText(sel);
  const Who = who.charAt(0).toUpperCase() + who.slice(1);
  switch (mod.kind) {
    case 'damage':
      return `${Who} deals ${pct(mod.pct)} more damage and healing`;
    case 'cooldown':
      return `${Who} recovers ${pct(mod.pct)} faster`;
    case 'cost':
      return `${Who} costs ${pct(mod.pct)} less`;
    case 'crit':
      return `${Who} has +${pct(mod.pct)} critical strike chance`;
    case 'castSpeed':
      return `${Who} casts ${pct(mod.pct)} faster`;
    case 'charge':
      return `${Who} gains a charge`;
    case 'extraTarget':
      return `${Who} hits ${mod.n} extra target${mod.n === 1 ? '' : 's'}`;
    case 'root':
      return `${Who} also roots for ${(mod.ms / 1000).toFixed(mod.ms % 1000 ? 1 : 0)} s`;
  }
}

// ---------------------------------------------------------------------------
// Notables (30) and keystones (3)
// ---------------------------------------------------------------------------

const n = (id: string, name: string, text: string, tank: number, healer: number, dps: number): NotableDef => ({ id, name, text, value: { tank, healer, dps } });

export const GRID_NOTABLES: readonly NotableDef[] = [
  // Warrior region (Might)
  n('grindstone', 'Grindstone', 'Kills restore 3% of your maximum health.', 3, 1.5, 2),
  n('second_breath', 'Second Breath', 'The first ability you use after a fight costs nothing.', 2, 2, 2),
  n('anvil_heart', 'Anvil Heart', 'Below 50% health you take 12% less damage.', 3.5, 2, 1.5),
  n('unbroken_line', 'Unbroken Line', 'Once per fight, a killing blow leaves you at 1 health and heals you for 25%.', 4, 3, 3),
  n('hooves_of_war', 'Hooves of War', 'Your key-2 ability grants 8% more damage for 6 s.', 2.5, 0.5, 4),
  // Rogue region (Finesse)
  n('pack_hunter', 'Pack Hunter', '+4% damage for each enemy within 10 yd (up to 3).', 2.5, 0.5, 4),
  n('knifes_edge', "Knife's Edge", 'Critical hits restore 2% of your primary resource (once per second).', 1.5, 1.5, 3),
  n('quickstep', 'Quickstep', 'Avoiding an attack grants 8% haste for 4 s.', 2.5, 0.5, 1.5),
  n('ghost_step', 'Ghost Step', 'Kills grant 30% movement speed and 6% damage for 5 s.', 1.5, 0.5, 3),
  n('patient_blade', 'Patient Blade', 'Out of combat, your next ability is a guaranteed critical hit.', 1, 1, 2.5),
  // Ranger region (Finesse / Precision)
  n('quarry', 'Quarry', 'Your key-2 ability marks the target: it takes 5% more damage for 6 s.', 2.5, 0.5, 4),
  n('fletchers_rhythm', "Fletcher's Rhythm", 'Every 4th use of your key-1 ability makes your next key-2 ability free.', 1.5, 1.5, 3),
  n('wind_at_back', 'Wind at Your Back', '+10% movement speed and +2% haste.', 2, 2.5, 2.5),
  n('twin_fletch', 'Twin Fletch', 'Critical hits have a 20% chance to loose a second arrow for 30% of your power.', 1.5, 0.5, 3.5),
  n('long_hunt', 'Long Hunt', 'Kills grant 3% haste for 10 s, stacking 3 times.', 2, 1, 3.5),
  // Mage region (Arcana / Precision)
  n('spellweave', 'Spellweave', 'Your key-1 ability takes 1 s off your AoE ability\'s cooldown.', 2, 1.5, 3),
  n('static_mind', 'Static Mind', 'Critical hits grant 1% haste for 6 s, stacking 5 times.', 2, 2, 3.5),
  n('glass_clarity', 'Glass Clarity', '+6% damage while above 80% health.', 2, 1, 3.5),
  n('afterglow', 'Afterglow', 'Your key-2 ability also looses a star-bolt for 25% of your power as arcane damage.', 2, 0.5, 4),
  n('deep_font', 'Deep Font', 'Every 10 s, restore 5% of your primary resource.', 2, 3.5, 3),
  // Acolyte region (Arcana / Endurance)
  n('kindred_light', 'Kindred Light', 'Your heals also mend you for 2% of your maximum health (once per second).', 2.5, 3.5, 1),
  n('hearth_pulse', 'Hearth Pulse', 'Every 6 s in combat, heal yourself and allies within 10 yd for 1.5% of your maximum health.', 2.5, 3.5, 1.5),
  n('rekindle', 'Rekindle', 'Dropping below 30% health heals you for 20% (once a minute).', 3.5, 3, 2),
  n('mercy_tide', 'Mercy Tide', 'Your key-3 ability also gives its target 6% damage reduction for 5 s.', 2, 3.5, 0.5),
  n('candlewake', 'Candlewake', 'Every 4th ability you use grants 6% damage and healing for 6 s.', 2, 3.5, 3),
  // Borders
  n('red_tempo', 'Red Tempo', 'Your auto-attacks have a 20% chance to grant 6% haste for 5 s.', 3, 0.5, 3),
  n('foxs_slip', "Fox's Slip", 'Your movement ability grants 8% damage for 4 s.', 2, 1, 3),
  n('clear_sight', 'Clear Sight', '+3% critical strike chance. Critical hits restore 1% of your primary resource (twice a second at most).', 2, 2.5, 3.5),
  n('starlit_mind', 'Starlit Mind', '+3% haste and +4% healing done.', 2, 4, 3),
  n('bulwark_psalm', 'Bulwark Psalm', '+5% maximum health and +6% healing received.', 4, 3, 1.5),
];

export const GRID_KEYSTONES: readonly KeystoneDef[] = [
  { id: 'glass_vow', name: 'Glass Vow', text: '+25% damage and healing done.', cost: '−20% maximum health.' },
  { id: 'iron_root', name: 'Iron Root', text: 'You take 15% less damage.', cost: 'You can never land a critical hit.' },
  { id: 'blood_price', name: 'Blood Price', text: 'Your abilities cost no resource.', cost: 'Instead each one costs health: 1.2% of your maximum health per 10% of the pool it would have cost.' },
];

/** Future keystones (dormant stars at cap 30+): name + design note, shown greyed in the grid. */
export const GRID_DORMANT_KEYSTONES: readonly { name: string; note: string }[] = [
  { name: 'Hollow Crown', note: 'Heals from others are halved; your own heals on yourself are doubled.' },
  { name: 'Starbound Pact', note: 'Cooldowns recover 30% faster; every ability costs 20% more.' },
  { name: 'Quiet Mind', note: 'Resource regeneration doubled; damage −10%.' },
  { name: 'Ember Oath', note: 'Damage over time ticks 40% faster; direct hits deal 15% less.' },
  { name: 'Long Road', note: 'Offline gains +40%; out-of-combat health regeneration stops.' },
  { name: 'Shared Burden', note: 'Allies within 10 yd take 10% less damage; you take that damage instead.' },
  { name: 'Kingless', note: 'No keystone limit for notables in your region; other regions cost double.' },
];

// ---------------------------------------------------------------------------
// Region recipes (layout.ts)
// ---------------------------------------------------------------------------

export interface SpokeRecipe {
  /** Travel stats t1..t4 alternate these. */
  stats: [GridStat, GridStat];
  /** Cluster ring stat (entry + 4 ring nodes alternate ring, stats[0]). */
  ring: GridStat;
  notable: string;
}

export interface PocketRecipe {
  stats: [GridStat, GridStat];
  notable: string;
  slot: { sel: SlotSelector; mod: SlotMod };
}

export interface RegionRecipe {
  classId: string;
  theme: GridTheme;
  constellation: string;
  flavour: string;
  spokes: [SpokeRecipe, SpokeRecipe, SpokeRecipe];
  /** Slot nodes: off the left spoke, off the centre notable, off the right spoke. */
  slots: [{ sel: SlotSelector; mod: SlotMod }, { sel: SlotSelector; mod: SlotMod }, { sel: SlotSelector; mod: SlotMod }];
  /** in1 (toward the hub) stat. */
  inward: GridStat;
  utility: [GridIdle, GridIdle, GridIdle];
  pockets: [PocketRecipe, PocketRecipe];
}

export interface BorderRecipe {
  theme: GridTheme;
  stats: [GridStat, GridStat];
  notable: string;
  slot: { sel: SlotSelector; mod: SlotMod };
  utility: GridIdle;
  /** Active keystone on this border's outer ring (else a dormant star). */
  keystone: string | null;
}

const k = (key: '1' | '2' | '3' | '4' | '5' | '6' | '7'): SlotSelector => ({ key });

export const GRID_REGIONS: readonly RegionRecipe[] = [
  {
    classId: 'warrior',
    theme: 'might',
    constellation: 'The Anvil',
    flavour: 'Seven stars struck from one hammer-blow. Warriors begin here.',
    spokes: [
      { stats: ['armor', 'maxHp'], ring: 'armor', notable: 'grindstone' },
      { stats: ['damage', 'maxHp'], ring: 'damage', notable: 'second_breath' },
      { stats: ['armor', 'healingTaken'], ring: 'armor', notable: 'anvil_heart' },
    ],
    slots: [
      { sel: k('2'), mod: { kind: 'root', ms: 1000 } },
      { sel: k('1'), mod: { kind: 'damage', pct: 0.06 } },
      { sel: { tag: 'defensive' }, mod: { kind: 'charge' } },
    ],
    inward: 'maxHp',
    utility: ['oocSpeed', 'goldFind', 'autoWits'],
    pockets: [
      { stats: ['maxHp', 'armor'], notable: 'unbroken_line', slot: { sel: { tag: 'aoe' }, mod: { kind: 'damage', pct: 0.08 } } },
      { stats: ['damage', 'haste'], notable: 'hooves_of_war', slot: { sel: k('4'), mod: { kind: 'cooldown', pct: 0.1 } } },
    ],
  },
  {
    classId: 'rogue',
    theme: 'finesse',
    constellation: 'The Veiled Knife',
    flavour: 'A blade of faint stars, only seen from the corner of the eye. Rogues begin here.',
    spokes: [
      { stats: ['damage', 'haste'], ring: 'damage', notable: 'pack_hunter' },
      { stats: ['crit', 'damage'], ring: 'crit', notable: 'knifes_edge' },
      { stats: ['dodge', 'haste'], ring: 'dodge', notable: 'quickstep' },
    ],
    slots: [
      { sel: { tag: 'spender' }, mod: { kind: 'crit', pct: 0.05 } },
      { sel: k('1'), mod: { kind: 'crit', pct: 0.05 } },
      { sel: { tag: 'movement' }, mod: { kind: 'cooldown', pct: 0.12 } },
    ],
    inward: 'haste',
    utility: ['goldFind', 'oocSpeed', 'gatherSpeed'],
    pockets: [
      { stats: ['crit', 'moveSpeed'], notable: 'ghost_step', slot: { sel: k('2'), mod: { kind: 'damage', pct: 0.08 } } },
      { stats: ['damage', 'crit'], notable: 'patient_blade', slot: { sel: { tag: 'aoe' }, mod: { kind: 'extraTarget', n: 1 } } },
    ],
  },
  {
    classId: 'ranger',
    theme: 'finesse',
    constellation: 'The Hart',
    flavour: 'A running stag of nine stars, antlers raised. Rangers begin here.',
    spokes: [
      { stats: ['damage', 'crit'], ring: 'damage', notable: 'quarry' },
      { stats: ['haste', 'damage'], ring: 'haste', notable: 'fletchers_rhythm' },
      { stats: ['moveSpeed', 'crit'], ring: 'crit', notable: 'wind_at_back' },
    ],
    slots: [
      { sel: k('2'), mod: { kind: 'castSpeed', pct: 0.08 } },
      { sel: k('1'), mod: { kind: 'damage', pct: 0.06 } },
      { sel: { tag: 'aoe' }, mod: { kind: 'extraTarget', n: 1 } },
    ],
    inward: 'crit',
    utility: ['gatherSpeed', 'oocSpeed', 'profSkillUp'],
    pockets: [
      { stats: ['crit', 'damage'], notable: 'twin_fletch', slot: { sel: k('3'), mod: { kind: 'cooldown', pct: 0.1 } } },
      { stats: ['haste', 'damage'], notable: 'long_hunt', slot: { sel: { tag: 'interrupt' }, mod: { kind: 'cooldown', pct: 0.12 } } },
    ],
  },
  {
    classId: 'mage',
    theme: 'arcana',
    constellation: 'The Astrolabe',
    flavour: 'Rings within rings, turning so slowly no one has seen them move. Mages begin here.',
    spokes: [
      { stats: ['damage', 'regen'], ring: 'damage', notable: 'spellweave' },
      { stats: ['haste', 'crit'], ring: 'haste', notable: 'static_mind' },
      { stats: ['damage', 'crit'], ring: 'damage', notable: 'glass_clarity' },
    ],
    slots: [
      { sel: k('2'), mod: { kind: 'crit', pct: 0.05 } },
      { sel: k('1'), mod: { kind: 'castSpeed', pct: 0.06 } },
      { sel: { tag: 'aoe' }, mod: { kind: 'damage', pct: 0.06 } },
    ],
    inward: 'regen',
    utility: ['profSkillUp', 'offlineRate', 'gatherSpeed'],
    pockets: [
      { stats: ['damage', 'haste'], notable: 'afterglow', slot: { sel: { tag: 'defensive' }, mod: { kind: 'cooldown', pct: 0.12 } } },
      { stats: ['regen', 'crit'], notable: 'deep_font', slot: { sel: k('2'), mod: { kind: 'root', ms: 1000 } } },
    ],
  },
  {
    classId: 'acolyte',
    theme: 'arcana',
    constellation: 'The Candle',
    flavour: 'A single bright star with a trembling crown of light. Acolytes begin here.',
    spokes: [
      { stats: ['healing', 'regen'], ring: 'healing', notable: 'kindred_light' },
      { stats: ['healing', 'maxHp'], ring: 'healing', notable: 'hearth_pulse' },
      { stats: ['maxHp', 'cooldown'], ring: 'maxHp', notable: 'rekindle' },
    ],
    slots: [
      { sel: { tag: 'heal' }, mod: { kind: 'damage', pct: 0.06 } },
      { sel: k('1'), mod: { kind: 'damage', pct: 0.06 } },
      { sel: k('4'), mod: { kind: 'cooldown', pct: 0.1 } },
    ],
    inward: 'regen',
    utility: ['autoWits', 'offlineRate', 'goldFind'],
    pockets: [
      { stats: ['healing', 'haste'], notable: 'mercy_tide', slot: { sel: { tag: 'aoe' }, mod: { kind: 'extraTarget', n: 1 } } },
      { stats: ['damage', 'healing'], notable: 'candlewake', slot: { sel: { tag: 'defensive' }, mod: { kind: 'charge' } } },
    ],
  },
];

/** Borders, clockwise: border i lies between GRID_CLASS_ORDER[i] and [i + 1]. */
export const GRID_BORDERS: readonly BorderRecipe[] = [
  { theme: 'might', stats: ['damage', 'crit'], notable: 'red_tempo', slot: { sel: k('5'), mod: { kind: 'damage', pct: 0.06 } }, utility: 'goldFind', keystone: 'blood_price' },
  { theme: 'finesse', stats: ['crit', 'haste'], notable: 'foxs_slip', slot: { sel: k('1'), mod: { kind: 'crit', pct: 0.04 } }, utility: 'gatherSpeed', keystone: null },
  { theme: 'precision', stats: ['crit', 'damage'], notable: 'clear_sight', slot: { sel: k('2'), mod: { kind: 'cost', pct: 0.08 } }, utility: 'oocSpeed', keystone: 'glass_vow' },
  { theme: 'arcana', stats: ['haste', 'healing'], notable: 'starlit_mind', slot: { sel: k('6'), mod: { kind: 'cooldown', pct: 0.1 } }, utility: 'profSkillUp', keystone: null },
  { theme: 'endurance', stats: ['maxHp', 'healingTaken'], notable: 'bulwark_psalm', slot: { sel: k('7'), mod: { kind: 'cooldown', pct: 0.12 } }, utility: 'autoWits', keystone: 'iron_root' },
];

function pct(v: number): string {
  const p = Math.round(v * 1000) / 10;
  return `${p}%`;
}

// ---------------------------------------------------------------------------
// Points and Grid Keys (progression grid.ts reads these; docs/design/waystone-grid.md "Keys")
// ---------------------------------------------------------------------------

/** Main-quest steps that each pay +1 grid point when finished (Mossvale: five, all before level 10). */
export const GRID_POINT_STEPS: readonly string[] = ['mv_main_5_barrow', 'mv_main_7_hearth', 'mv_main_8_lake', 'mv_main_9_heights', 'mv_main_9_bramblewood'];

/** Grid Key sources. Each pays one key, once per character. */
export const GRID_KEY_SOURCES = {
  /** Main-quest steps (the Glowing Ruin holds the first key). */
  steps: ['mv_main_4_ruin'] as readonly string[],
  /** Side quests (turned in). */
  quests: ['sq_offkey_2'] as readonly string[],
  /** First clear of a levelled dungeon. */
  dungeons: ['hollow_barrow'] as readonly string[],
  /** Rare named mobs (first kill). */
  rares: ['old_mossback', 'foreman_tallowick', 'greymaw'] as readonly string[],
  /** Secret chests (`mapId:secretId`, opened by the hero). */
  secrets: ['mossvale:hermit_cache', 'mossvale:kings_hand', 'mossvale:sky_starfall'] as readonly string[], // +glide: the Starfall Spire's chest
  /** The Tower's Advancement Trial (choosing a specialisation). */
  tower: true,
} as const;
